import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { contexto as api, type EstadoDaIa as Estado } from '../api/roteamento'
import { ErroApi } from '../api/client'
import './estadoDaIa.css'

const LOCALE = 'pt-BR'

type Tradutor = (chave: string, opcoes?: Record<string, unknown>) => string

/** `02h02` — a hora como a equipe a escreve. */
function hora(quando: Date): string {
  const partes = new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit', hour12: false })
    .formatToParts(quando)

  const de = (tipo: string) => partes.find((p) => p.type === tipo)?.value ?? '00'

  return `${de('hour')}h${de('minute')}`
}

/** Meia-noite local do dia — para contar dias de calendário, e não blocos de 24 h. */
const diaDe = (quando: Date) => new Date(quando.getFullYear(), quando.getMonth(), quando.getDate()).getTime()

/**
 * O dia em relação a hoje, na hora LOCAL de quem lê: `hoje`, `amanhã`, `ontem`, o dia da
 * semana (`quarta`) dentro de uma semana, e a data além disso.
 *
 * ⚠️ O servidor manda UTC. *"Até 05:02"* era o que o log dizia em 05/10 — e 05:02 UTC são
 * 02h02 em Brasília, que é o que a pessoa diante da tela precisa ler.
 */
function quando(iso: string, t: Tradutor, agora: Date = new Date()): string {
  const data = new Date(iso)
  const dias = Math.round((diaDe(data) - diaDe(agora)) / 86_400_000)

  const dia =
    dias === 0
      ? t('contexto.ia.dia.hoje')
      : dias === 1
        ? t('contexto.ia.dia.amanha')
        : dias === -1
          ? t('contexto.ia.dia.ontem')
          : Math.abs(dias) < 7
            ? new Intl.DateTimeFormat(LOCALE, { weekday: 'long' }).format(data).replace('-feira', '')
            : new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit' }).format(data)

  return t('contexto.ia.quando', { hora: hora(data), dia })
}

/** O motivo em uma frase para leigo — o que está acontecendo, sem o nome do mecanismo. */
function frase(t: Tradutor, estado: Estado): string {
  const base = `contexto.ia.motivo.${estado.motivo}`

  switch (estado.motivo) {
    case 'SilencioPorFalaHumana':
      return t(estado.deOutraConversa ? `${base}.outra` : `${base}.esta`, {
        conversa: estado.deOutraConversa,
        desde: estado.desde ? quando(estado.desde, t) : '',
      })

    case 'PausadaPorTransferencia':
      return t(estado.time ? `${base}.comTime` : `${base}.semTime`, {
        time: estado.time,
        detalhe: estado.detalhe,
        desde: estado.desde ? quando(estado.desde, t) : '',
      })

    case 'PausadaPorTrava':
    case 'TravaDeLaco':
      return t(base, { detalhe: estado.detalhe ?? '' })

    default:
      // Motivo que esta versão da tela não conhece: diz que está calada, e o que o servidor
      // souber. Calar a faixa seria voltar ao defeito que ela existe para resolver.
      return t(base, { defaultValue: t('contexto.ia.motivo.desconhecido', { detalhe: estado.detalhe ?? '' }) })
  }
}

type Gesto = 'liberar' | 'pausar'

/**
 * A faixa de estado da IA, no topo do painel de Contexto (ficha 310).
 *
 * 🔴 **Nasceu de uma apresentação, em 05/10:** o Vicente tinha respondido um contato pelo
 * painel de manhã; à tarde o contato escreveu de novo, em conversas novas, e a IA não
 * respondeu. Era o silêncio de 12 h depois da fala de uma pessoa — e na sala ninguém sabia.
 * O servidor sabia, e disse só ao log.
 *
 * ⭐ **A tela não decide nada:** o motivo, o "até quando" e se há botão vêm do servidor, que
 * responde com a mesma regra que cala a IA. Aqui só se escreve a frase e a hora local.
 *
 * ⚠️ **Sem depender de cor** (UI-09): o estado está no texto (*"IA atendendo"*, *"IA
 * calada"*) e no símbolo; a cor da faixa só reforça.
 *
 * 🔴 **Os dois gestos pedem confirmação NA FAIXA** (ficha 317) — "Liberar a IA", na faixa
 * calada, e "Pausar a IA", na faixa atendendo. O primeiro clique só mostra o que vai
 * acontecer; quem chama o servidor é o segundo. Sem `window.confirm`: dentro do iframe do
 * Chatwoot o navegador pode bloqueá-lo, e o clique viraria um "sim" que ninguém deu.
 *
 * ⚠️ **Contra o servidor anterior à 317 a faixa é a de antes:** sem `podePausar` não há botão
 * de pausar, e sem `liberadaPor` não há a linha de quem liberou.
 */
export function EstadoDaIa({
  estado: recebido,
  conversaExterna,
}: {
  /** Nulo quando o portal ainda não registrou a conversa: não há o que afirmar. */
  estado: Estado | null | undefined
  conversaExterna: number | undefined
}) {
  const { t } = useTranslation()

  const [estado, setEstado] = useState(recebido)
  const [confirmando, setConfirmando] = useState<Gesto | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [feito, setFeito] = useState<Gesto | null>(null)
  const [recusa, setRecusa] = useState<string | null>(null)

  // O painel recarrega (outra conversa, volta à aba, botão de atualizar): vale o que veio.
  useEffect(() => {
    setEstado(recebido)
    setConfirmando(null)
    setFeito(null)
    setRecusa(null)
  }, [recebido])

  if (!estado) return null

  async function executar(gesto: Gesto) {
    if (!conversaExterna) return

    setEnviando(true)
    setRecusa(null)

    try {
      const resposta =
        gesto === 'liberar' ? await api.liberarIa(conversaExterna) : await api.pausarIa(conversaExterna)

      setEstado(resposta.ia)
      setFeito(gesto)
    } catch (erro) {
      // 409: o estado mudou entre a tela abrir e o clique (a conversa foi transferida, ganhou
      // etiqueta, alguém já a pausou), ou o gesto não tem efeito ali. O servidor manda o estado
      // de agora e a explicação.
      const corpo = erro instanceof ErroApi ? (erro.corpo as { detail?: string; ia?: Estado } | null) : null

      if (corpo?.ia) setEstado(corpo.ia)
      setFeito(null)
      setRecusa(corpo?.detail ?? t(gesto === 'liberar' ? 'contexto.ia.falhou' : 'contexto.ia.falhouPausar'))
    } finally {
      setEnviando(false)
      setConfirmando(null)
    }
  }

  const aviso = recusa && (
    <p className="estadoDaIa__texto estadoDaIa__recusa" role="alert">
      {recusa}
    </p>
  )

  /** O botão do gesto e, depois do primeiro clique, a confirmação no lugar dele. */
  function acao(gesto: Gesto, pergunta: string) {
    if (!conversaExterna) return null

    if (confirmando !== gesto)
      return (
        <div className="estadoDaIa__acoes">
          <button type="button" className="botao botao--secundario" onClick={() => setConfirmando(gesto)}>
            {t(`contexto.ia.${gesto}`)}
          </button>
        </div>
      )

    return (
      <div className="estadoDaIa__confirmacao">
        <p className="estadoDaIa__texto">
          <strong>{pergunta}</strong>
        </p>

        <div className="estadoDaIa__acoes">
          <button type="button" className="botao botao--primario" disabled={enviando} onClick={() => executar(gesto)}>
            {enviando
              ? t(gesto === 'liberar' ? 'contexto.ia.liberando' : 'contexto.ia.pausando')
              : t(gesto === 'liberar' ? 'contexto.ia.liberarConfirmado' : 'contexto.ia.pausarConfirmado')}
          </button>
          <button
            type="button"
            className="botao botao--discreto"
            disabled={enviando}
            onClick={() => setConfirmando(null)}
          >
            {t('comum.cancelar')}
          </button>
        </div>
      </div>
    )
  }

  if (estado.atendendo)
    return (
      <section className="estadoDaIa estadoDaIa--atendendo" role="status" aria-label={t('contexto.ia.rotulo')}>
        <p className="estadoDaIa__titulo">
          {t('contexto.ia.atendendo')}
        </p>

        {estado.liberadaPor && estado.liberadaEm && (
          <p className="estadoDaIa__apoio">
            {t('contexto.ia.liberadaPor', { nome: estado.liberadaPor, quando: quando(estado.liberadaEm, t) })}
          </p>
        )}

        {feito === 'liberar' && <p className="estadoDaIa__texto">{t('contexto.ia.proximaMensagem')}</p>}

        {aviso}

        {estado.podePausar &&
          acao('pausar', t('contexto.ia.confirmarPausar', { count: estado.horasDaPausa ?? 0 }))}
      </section>
    )

  const como = t(`contexto.ia.como.${estado.motivo}`, { defaultValue: '' })

  return (
    <section className="estadoDaIa estadoDaIa--calada" role="status" aria-label={t('contexto.ia.rotulo')}>
      <p className="estadoDaIa__titulo">
        {t(estado.motivo === 'TravaDeLaco' ? 'contexto.ia.vaiParar' : 'contexto.ia.calada')}
        {estado.ate && <span className="estadoDaIa__ate"> {t('contexto.ia.ate', { ate: quando(estado.ate, t) })}</span>}
      </p>

      {/* Logo depois do clique em "Pausar", a frase é a do gesto: ninguém "respondeu nesta conversa". */}
      <p className="estadoDaIa__texto">
        {feito === 'pausar' && estado.motivo === 'SilencioPorFalaHumana'
          ? t('contexto.ia.pausadaAgora')
          : frase(t, estado)}
      </p>

      {como && <p className="estadoDaIa__texto">{como}</p>}

      {aviso}

      {estado.podeLiberar && acao('liberar', t('contexto.ia.confirmarLiberar'))}
    </section>
  )
}
